import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-green-transparent_1.svg';
import backgroundImage from './background_img.jpg';
import { FaShieldAlt } from 'react-icons/fa';
import { useAuth } from './AuthContext'; // Import the useAuth hook

export default function OTPVerification() {
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { verifyOtpAndLogin, accessToken } = useAuth();
    const navigate = useNavigate();

    // Redirects if the user successfully logs in
    useEffect(() => {
        if (accessToken) {
            navigate('/home');
        }
    }, [accessToken, navigate]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        if (!otp || otp.length !== 6 || !/^\d{6}$/.test(otp)) {
            setError('Please enter a valid 6-digit OTP code.');
            setLoading(false);
            return;
        }

        try {
            await verifyOtpAndLogin(otp);
            // On success, the useEffect above will handle the redirect.
        } catch (error) {
            const errorData = error.response?.data;
            let errorMessage = 'An unknown error occurred.';

            if (typeof errorData?.detail === 'string') {
                errorMessage = errorData.detail;
            } else if (Array.isArray(errorData?.detail)) {
                errorMessage = errorData.detail[0]?.msg || 'Invalid input provided.';
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page-container">
            <div className="login-image-section" style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className="login-image-content">
                    <img src={logo} alt="Wysupp Logo" className="login-logo-image" />
                </div>
            </div>
            <div className="login-form-section">
                <div className="login-form-wrapper">
                    <h1 className="login-title">Two-Factor Authentication</h1>
                    <p className="login-subtitle">Please enter the 6-digit code from your authenticator app to continue.</p>

                    {error && <p className="login-errmsg">{error}</p>}

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="login-input-group">
                            <FaShieldAlt className="login-input-icon" />
                            <input
                                type="text"
                                id="otp"
                                name="otp"
                                value={otp}
                                /* Keep only digits: a pasted code often arrives as
                                   "123 456", which used to fail the check below with a
                                   misleading message. */
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="6-Digit Code"
                                maxLength="6"
                                /* one-time-code stops Chrome treating this form as a
                                   saveable credential. Without it Chrome offers to save
                                   the OTP, then autofills that stale 6-digit value on the
                                   next login - which passes the 6-digit check here and is
                                   rejected by the server as an invalid code. */
                                autoComplete="one-time-code"
                                inputMode="numeric"
                                required
                                autoFocus
                            />
                        </div>

                        <button className="login-button" disabled={loading}>
                            {loading ? 'Verifying...' : 'Verify Code'}
                        </button>
                    </form>
                </div>
            </div>
            <style>{`
                :root {
                    --login-bg: #1a202c;
                    --login-form-bg: #2d3748;
                    --login-text: #e2e8f0;
                    --login-light-text: #a0aec0;
                    --login-green-accent: #48bb78;
                    --login-input-bg: #1a202c;
                    --login-border-color: #4a5568;
                }

                .login-page-container, .login-page-container * {
                    box-sizing: border-box;
                }

                .login-page-container {
                    display: grid;
                    grid-template-columns: 1.2fr 0.8fr;
                    height: 100vh;
                    font-family: 'Inter', sans-serif;
                    background-color: var(--login-bg);
                    overflow: hidden;
                }

                .login-image-section {
                    background-size: cover;
                    background-position: center;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #ffffff;
                    width: 65vw;
                }

                .login-image-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(26, 32, 44, 0.6);
                    z-index: 0;
                }

                .login-image-content {
                    position: relative;
                    z-index: 1;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1.5rem;
                }

                .login-logo-image {
                    width: 400px;
                    height: 400px;
                }

                .login-app-name {
                    font-size: 2.8rem;
                    font-weight: 700;
                    letter-spacing: 1px;
                }

                .login-form-section {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: var(--login-form-bg);
                    padding: 2rem;
                    width: 35vw;
                    border-left: 5px solid #16362e;
                }

                .login-form-wrapper {
                    width: 100%;
                    max-width: 320px; /* Adjusted for OTP content */
                    padding: 2.5rem 1.5rem;
                    background-color: var(--login-form-bg);
                    border-radius: 8px;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
                }

                .login-title {
                    font-size: 1.8rem; /* Adjusted for longer title */
                    font-weight: 700;
                    color: var(--login-text);
                    margin-bottom: 0.75rem;
                    text-align: center;
                }

                .login-subtitle {
                    font-size: 0.95rem;
                    color: var(--login-light-text);
                    margin-bottom: 2rem;
                    text-align: center;
                    line-height: 1.5;
                }

                .login-errmsg {
                    background-color: rgba(239, 68, 68, 0.1);
                    color: #fca5a5;
                    border: 1px solid rgba(239, 68, 68, 0.3);
                    padding: 0.75rem;
                    border-radius: 6px;
                    margin-bottom: 1.5rem;
                    font-size: 0.9rem;
                    text-align: center;
                }

                .login-form {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem; /* Increased gap */
                }

                .login-input-group {
                    position: relative;
                }

                .login-input-group input {
                    width: 100%;
                    height: 2.5rem;
                    padding: 1rem 1rem 1rem 3rem;
                    background-color: var(--login-input-bg);
                    border: 1px solid var(--login-border-color);
                    border-radius: 6px;
                    color: var(--login-text);
                    font-size: 1.2rem; /* Larger font for OTP code */
                    text-align: center; /* Center the OTP code */
                    letter-spacing: 0.5rem; /* Space out the digits */
                    transition: border-color 0.2s;
                }

                .login-input-group input:focus {
                    outline: none;
                    border-color: var(--login-green-accent);
                }
                
                .login-input-group input::placeholder {
                    letter-spacing: normal;
                }

                .login-input-icon {
                    position: absolute;
                    top: 50%;
                    left: 1rem;
                    transform: translateY(-50%);
                    color: var(--login-light-text);
                    font-size: 1.1rem;
                }

                .login-button {
                    background-color: var(--login-green-accent);
                    color: var(--login-bg);
                    border: none;
                    border-radius: 6px;
                    padding: 0.9rem;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    margin-top: 1rem;
                }

                .login-button:hover {
                    background-color: #38a169;
                }

                .login-button:disabled {
                    background-color: #4a5568;
                    cursor: not-allowed;
                }

                @media (max-width: 900px) {
                    .login-page-container {
                        grid-template-columns: 1fr;
                    }
                    .login-image-section {
                        display: none;
                    }
                    .login-form-section {
                        background: var(--login-bg);
                    }
                    .login-form-wrapper {
                        max-width: 350px;
                        padding: 2rem 1rem;
                    }
                }
            `}</style>
        </div>
    );
}